import {HttpClient}                                                 from '@angular/common/http';
import {Component, OnInit, ViewChild}                               from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators}              from '@angular/forms';
import {MatDialog, MatDialogRef}                                    from '@angular/material/dialog';
import {MatPaginator, PageEvent}                                    from '@angular/material/paginator';
import {MatSnackBar}                                                from '@angular/material/snack-bar';
import {MatTableDataSource}                                         from '@angular/material/table';
import {ActivatedRoute, Router, UrlTree}                            from '@angular/router';
import {Manufacturer}                                               from '@src/app/admin/manufacturers/models/manufacturer.model';
import {ManufacturerService}                                        from '@src/app/admin/manufacturers/services/manufacturer.service';
import {ManufacturerProductSpecificationSheet, Product, ProductManufacturer} from '@src/app/admin/products/models/product.model';
import {Breadcrumb}                                                 from '@src/app/shared/models/breadcrumb.model';
import {Button}                                                     from '@src/app/shared/models/button.model';
import {ApiService}                                                 from '@src/app/shared/services/api.service';
import {SettingsService}                                            from '@src/app/core/services/settings.service';
import {environment}                                                from '@src/environments/environment';
import {query}                                                      from 'js-query-builder';
import moment                                                       from 'moment';
import {AdditionalPriceTierModalComponent}                          from '../additional-price-tier-modal/additional-price-tier-modal.component';
import {EditPriceTierModalComponent}                                from '../edit-price-tier-modal/edit-price-tier-modal.component';
import {PriceUploadModalComponent}                                  from '../price-upload-modal/price-upload-modal.component';
import {DeletePriceTierModalComponent}                              from '../delete-price-tier-modal/delete-price-tier-modal.component';
import {DialogComponent}                                            from '@src/app/shared/components/dialog/dialog.component';

@Component({
    standalone: false,
    selector: 'app-pricing-view',
    templateUrl: './pricing-view.component.html',
    styleUrls: ['./pricing-view.component.css']
})
export class PricingViewComponent implements OnInit
{
    @ViewChild(MatPaginator) public paginator!: MatPaginator;
    public actionType: 'add' | 'edit' = 'add';
    public productManufacturer: ProductManufacturer;
    public manufacturers: Manufacturer[] = [];
    public isDynamicallyPriced: boolean = false;
    public loading: boolean = false;
    public manufacturerForm: FormGroup;
    public priceChanges: any[] = [];
    public pageTitle: string = 'Add Pricing';
    public breadcrumbs: Breadcrumb[] = [];
    public buttons: Button[] = [];
    public currentDate: Date = new Date();
    public dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    public displayColumns = ['tier', 'operative_cost', 'subcontractor_cost', 'material_cost', 'sell_price', 'effective_from', 'effective_to', 'action'];
    public totalRows: number = 0;
    public pageSize: number = 10;
    public currentPage: number = 0;
    public pageSizeOptions: number[] = [5, 10, 25, 100];
    public specificationSheetFiles: File[] = [];
    public specificationSheetUploading: boolean = false;
    public specificationSheetRestoring: boolean = false;
    private allManufacturers: Manufacturer[] = [];
    private linkedManufacturerIds: Set<number> = new Set<number>();

    public get specificationSheets(): ManufacturerProductSpecificationSheet[]
    {
        return this.productManufacturer?.specification_sheets ?? [];
    }

    public get hiddenDefaultSpecificationSheets(): ManufacturerProductSpecificationSheet[]
    {
        return this.productManufacturer?.hidden_default_specification_sheets ?? [];
    }

    public get hasSpecificationSheets(): boolean
    {
        return this.specificationSheets.length > 0;
    }

    public get hasHiddenDefaultSpecificationSheets(): boolean
    {
        return this.hiddenDefaultSpecificationSheets.length > 0;
    }

    public get pricingEnabled(): boolean
    {
        return this.settingsService.isPricingEnabled();
    }

    public get pricingTiersArray()
    {
        return this.manufacturerForm.controls['pricing_tiers'] as FormArray;
    }

    constructor(
        public snackBar: MatSnackBar,
        private manufacturerService: ManufacturerService,
        private apiService: ApiService,
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private httpClient: HttpClient,
        public dialog: MatDialog,
        private settingsService: SettingsService,
    )
    {
        this.breadcrumbs = [
            {
                title: 'Home',
                url: '/app/',
                active: false
            },
            {
                title: 'Products',
                active: false,
                url: '/app/products/',
            },
            {
                title: this.pricingEnabled ? 'Edit Pricing' : 'Manage Manufacturer',
                active: true,
            },
        ];

        this.buttons = [this.buildSaveButton()];

        this.manufacturerForm = this.fb.group({
            product_id: [null, Validators.required],
            manufacturer_id: [null, Validators.required],
            effective_from: [null],
            tier: [0, [Validators.required, Validators.min(0)]],
            operative_cost: [0, [Validators.required, Validators.min(0)]],
            subcontractor_cost: [0, [Validators.required, Validators.min(0)]],
            material_cost: [0, [Validators.required, Validators.min(0)]],
            sell_price: [0, [Validators.required, Validators.min(0)]],
            pricing_tiers: this.fb.array([])
        });

        this.activatedRoute.params.subscribe(params => {

            if (params['price_id'] != 'create') {
                this.actionType = 'edit';
                this.loadManufacturerProduct(params['id'], params['price_id']);
            } else {
                this.apiService.get(`products/${params['id']}`).subscribe({
                    next: response => {
                        this.isDynamicallyPriced = response.data.measure.id <= 5;
                        this.setLinkedManufacturerIds(response.data);
                    },
                    error: error => this.apiService.handleError(error)
                });

                this.manufacturerForm = this.fb.group({
                    product_id: [params['id'], Validators.required],
                    manufacturer_id: ['', Validators.required],
                    effective_from: [this.pricingEnabled ? '' : this.currentDate],
                    tier: [0, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    operative_cost: [0, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    subcontractor_cost: [0, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    material_cost: [0, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    sell_price: [0, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    pricing_tiers: this.fb.array([])
                });
            }

        });
    }

    public ngOnInit(): void
    {
        // NOTE: Ideally this should be filters with a where not in or something like that?
        this.manufacturerService.GetManufacturers(0, 0, true).subscribe(
            response => {
                this.allManufacturers = response.data ?? [];
                this.updateAvailableManufacturers();
            },
            response => this.manufacturerService.ErrorHandler(response)
        );
    }

    public loadPrices(): void
    {
        const url = query(`products/prices`)
            .filter('manufacturer_product_id', this.productManufacturer?.id)
            .filter('by_date', 'current')
            .fields({
                manufacturer_prices: ['id', 'manufacturer_product_id', 'tier', 'operative_cost', 'subcontractor_cost', 'material_cost', 'sell_price', 'effective_from', 'effective_to', 'created_at', 'updated_at']
            })
            .param('page[number]', this?.currentPage + 1)
            .param('page[size]', this?.pageSize)
            .sort('tier')
            .build();

        this.apiService.get(url).subscribe({
            next: response => {
                const responseData = this.isDynamicallyPriced ? response.data : response.data.filter(c => c.tier != '0.00');

                this.pricingTiersArray.clear();

                this.dataSource = new MatTableDataSource(responseData);
                if (this.paginator) {
                    this.paginator.pageIndex = this.currentPage;
                }
                this.totalRows = response.meta?.total ?? responseData.length;

                this.priceChanges.forEach((priceChange) => {
                    const dataIndex = this.dataSource.data.findIndex(item => item.id === priceChange.id);

                    if (dataIndex !== -1) {
                        this.dataSource.data[dataIndex] = priceChange;
                    }
                });

                this.dataSource._updateChangeSubscription();
            },
            error: error => this.apiService.handleError(error)
        });
    }

    public setSpecificationSheetFiles(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        this.specificationSheetFiles = Array.from(input.files ?? []);
    }

    public getSelectedSpecificationSheetFileNames(): string
    {
        return this.specificationSheetFiles.length ? this.specificationSheetFiles.map(file => file.name).join(', ') : 'No files selected';
    }

    public uploadSpecificationSheets(input?: HTMLInputElement): void
    {
        if (!this.productManufacturer?.id || !this.specificationSheetFiles.length || this.specificationSheetUploading) {
            return;
        }

        const formData = new FormData();
        this.specificationSheetFiles.forEach(file => formData.append('specification_sheets[]', file));
        this.specificationSheetUploading = true;

        this.apiService.post(`products/manufacturer/${this.productManufacturer.id}/specification-sheets`, formData).subscribe({
            next: () => {
                this.specificationSheetUploading = false;
                this.specificationSheetFiles = [];

                if (input) {
                    input.value = '';
                }

                this.refreshProductManufacturer();
                this.snackBar.open('Specification sheet files uploaded successfully.', 'Ok.', {duration: 4000});
            },
            error: error => {
                this.specificationSheetUploading = false;
                this.apiService.handleError(error);
            }
        });
    }

    public removeSpecificationSheet(specificationSheet: ManufacturerProductSpecificationSheet): void
    {
        if (!this.productManufacturer?.id || !specificationSheet?.id) {
            return;
        }

        (this.apiService.delete(`products/manufacturer/${this.productManufacturer.id}/specification-sheets`, specificationSheet.id) as any).subscribe({
            next: () => {
                this.refreshProductManufacturer();
                this.snackBar.open('Specification sheet removed successfully.', 'Ok.', {duration: 4000});
            },
            error: error => this.apiService.handleError(error)
        });
    }

    public restoreDefaultSpecificationSheets(): void
    {
        if (!this.productManufacturer?.id || !this.hasHiddenDefaultSpecificationSheets || this.specificationSheetRestoring) {
            return;
        }

        this.specificationSheetRestoring = true;

        this.apiService.post(`products/manufacturer/${this.productManufacturer.id}/specification-sheets/restore-defaults`, {}).subscribe({
            next: response => {
                this.productManufacturer = response.data;
                this.specificationSheetRestoring = false;
                this.snackBar.open('Default specification sheet files restored successfully.', 'Ok.', {duration: 4000});
            },
            error: error => {
                this.specificationSheetRestoring = false;
                this.apiService.handleError(error);
            }
        });
    }

    public downloadSpecificationSheet(specificationSheet: ManufacturerProductSpecificationSheet): void
    {
        if (!specificationSheet?.url) {
            return;
        }

        window.open(specificationSheet.url, '_blank', 'noopener');
    }

    public canPreviewSpecificationSheet(specificationSheet: ManufacturerProductSpecificationSheet): boolean
    {
        return this.isPdfSpecificationSheet(specificationSheet) || this.isWordSpecificationSheet(specificationSheet);
    }

    public getSpecificationSheetPreviewIcon(specificationSheet: ManufacturerProductSpecificationSheet): string
    {
        if (this.isPdfSpecificationSheet(specificationSheet)) {
            return 'picture_as_pdf';
        }

        if (this.isWordSpecificationSheet(specificationSheet)) {
            return 'description';
        }

        return 'insert_drive_file';
    }

    public onSubmit(): void
    {
        if (this.manufacturerForm.valid) {
            const formData = this.manufacturerForm.getRawValue();
            formData.effective_from = this.resolveEffectiveFromDate();

            if (!this.pricingEnabled) {
                formData.tier = 0;
                formData.operative_cost = formData.operative_cost ?? 0;
                formData.subcontractor_cost = formData.subcontractor_cost ?? 0;
                formData.material_cost = formData.material_cost ?? 0;
                formData.sell_price = formData.sell_price ?? 0;
            }

            if (this.actionType === 'edit') {
                this.apiService.update('products/prices', this.productManufacturer?.default_price?.id, formData).subscribe({
                    next: response => this.successCall(response),
                    error: error => this.errorCall(error)
                });
            } else {
                this.apiService.create('products/prices', formData).subscribe({
                    next: response => this.successCall(response),
                    error: error => this.errorCall(error)
                });
            }
        } else {
            this.snackBar.open('Please fill all required fields', 'Close', {
                duration: 3000,
                panelClass: ['error-snackbar']
            });
        }
    }

    public editPriceTier(priceTier: any): void
    {
        // Open your modal here and pass the priceTier data
        let priceForm = this.dialog.open(EditPriceTierModalComponent, {
            width: '50%',
            data: {
                priceTier: {
                    ...priceTier,
                    manufacturer_id: this.productManufacturer?.manufacturer.id,
                    product_id: this.productManufacturer?.product.id,
                },
                type: 'edit'
            }
        });

        priceForm.afterClosed().subscribe(result => {
            if (result) {
                this.loadPrices();
            }
        });
    }

    public addPriceTier(): void
    {
        let priceForm = this.dialog.open(EditPriceTierModalComponent, {
            width: '50%',
            data: {
                priceTier: {
                    manufacturer_id: this.productManufacturer?.manufacturer.id,
                    product_id: this.productManufacturer?.product.id,
                },
                type: 'add'
            }
        });

        priceForm.afterClosed().subscribe(result => {
            if (result) {
                this.loadPrices();
            }
        });
    }

    public deletePriceTier(priceTier: any): void
    {
        let removalForm = this.dialog.open(DeletePriceTierModalComponent, {
            width: '40%',
            data: {
                priceTier: priceTier,
            }
        });

        removalForm.afterClosed().subscribe(result => {
            if (result) {
                this.loadPrices();
            }
        });
    }

    public confirmArchivePriceTier(priceTier: any): void
    {
        const confirmDialogRef = this.dialog.open(DialogComponent, {
            data: {
                title: 'Archive Pricing Tier',
                message: 'Are you sure you want to archive this pricing tier? You can\'t undo this action.',
                type: 'danger-outline',
                confirmButtonLabel: 'Archive'
            }
        });

        confirmDialogRef.afterClosed().subscribe(confirm => {
            if (confirm) {
                this.deletePriceTier(priceTier);
            }
        });
    }

    public openAdditionalPriceModal(): void
    {
        this.dialog.open(AdditionalPriceTierModalComponent, {
            width: '80%',
            data: {
                productManufacturer: this.productManufacturer,
                isDynamicallyPriced: this.isDynamicallyPriced,
            }
        });
    }

    public downloadTemplate()
    {
        this.httpClient.get(`${environment.apiUrl}/prices-export/${this.productManufacturer.id}/download`, {
            responseType: 'blob',
            observe: 'response'
        }).subscribe((response) => {
            const data = response.body;
            let filename = 'price-template.xlsx'; // Default filename

            // Try to get filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            let downloadURL = window.URL.createObjectURL(data);
            let link = document.createElement('a');

            link.href = downloadURL;
            link.download = filename;
            link.click();
        });
    }

    public uploadPrices(): void
    {
        const priceUploadDialog: MatDialogRef<PriceUploadModalComponent> = this.dialog.open(PriceUploadModalComponent, {
            width: '65%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {productManufacturer: this.productManufacturer}
        });

        priceUploadDialog.afterClosed().subscribe((): void => {
            this.loadPrices();
        });
    }

    public pageChanged(event: PageEvent): void
    {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadPrices();
    }

    protected isPdfSpecificationSheet(specificationSheet: ManufacturerProductSpecificationSheet): boolean
    {
        return specificationSheet?.content_type === 'application/pdf' || specificationSheet?.file_name?.toLowerCase().endsWith('.pdf');
    }

    protected isWordSpecificationSheet(specificationSheet: ManufacturerProductSpecificationSheet): boolean
    {
        return specificationSheet?.content_type === 'application/msword'
            || specificationSheet?.content_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            || specificationSheet?.file_name?.toLowerCase().endsWith('.doc')
            || specificationSheet?.file_name?.toLowerCase().endsWith('.docx');
    }

    private successCall(response: any): void
    {
        this.snackBar.open(this.pricingEnabled ? 'Price tier saved successfully' : 'Manufacturer saved successfully', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
        });

        let redirectUrl: string | UrlTree;

        if (this.actionType === 'add') {
            this.actionType = 'edit';
            redirectUrl = `/app/products/edit/${response.data?.product.id}/pricing/${response.data.manufacturer_product_id}`;
        } else {
            redirectUrl = `/app/products/edit/${response.data?.product.id}`;
        }

        this.router.navigateByUrl(redirectUrl);
    }

    private errorCall(error: any): void
    {
        this.apiService.handleError(error);
        this.snackBar.open(this.pricingEnabled ? 'Failed to save price tier' : 'Failed to save manufacturer', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar']
        });
    }

    private buildSaveButton(): Button
    {
        return {
            type: 'button',
            material: 'flat',
            color: 'primary',
            title: this.pricingEnabled ? 'Save Product' : 'Save Manufacturer',
            permission: 'add_products',
            callback: () => {
                this.onSubmit();
            }
        };
    }

    private resolveEffectiveFromDate(): string
    {
        const effectiveFrom = this.manufacturerForm.get('effective_from')?.value || this.currentDate;

        return moment(effectiveFrom).format('YYYY-MM-DD');
    }

    private setLinkedManufacturerIds(product: Product): void
    {
        if (this.actionType !== 'add') {
            return;
        }

        const manufacturerIds = product?.manufacturer_products?.map(productManufacturer => productManufacturer?.manufacturer?.id)
            .filter((manufacturerId): manufacturerId is number => typeof manufacturerId === 'number')
            ?? product?.manufacturers?.map(manufacturer => manufacturer.id)
                .filter((manufacturerId): manufacturerId is number => typeof manufacturerId === 'number')
            ?? [];

        this.linkedManufacturerIds = new Set<number>(manufacturerIds);
        this.updateAvailableManufacturers();
    }

    private updateAvailableManufacturers(): void
    {
        if (this.actionType !== 'add' || !this.linkedManufacturerIds.size) {
            this.manufacturers = [...this.allManufacturers];

            return;
        }

        this.manufacturers = this.allManufacturers.filter(manufacturer => !this.linkedManufacturerIds.has(manufacturer.id));
    }

    private loadManufacturerProduct(productId: string | number, manufacturerProductId: string | number): void
    {
        this.apiService.get(`products/manufacturer/${manufacturerProductId}`).subscribe({
            next: response => {
                this.productManufacturer = response.data;
                this.specificationSheetFiles = [];
                this.specificationSheetUploading = false;
                this.specificationSheetRestoring = false;

                this.isDynamicallyPriced = this.productManufacturer?.product.measure.id <= 5;
                this.isDynamicallyPriced && this.pricingEnabled ? this.buttons = [] : this.buttons = [this.buildSaveButton()];
                this.pageTitle = `${this.pricingEnabled ? 'Edit Pricing' : 'Manage Manufacturer'}: ${this.productManufacturer?.manufacturer.name}`;

                this.breadcrumbs = [
                    {
                        title: 'Home',
                        url: '/app/',
                        active: false
                    },
                    {
                        title: 'Products',
                        active: false,
                        url: '/app/products/',
                    },
                    {
                        title: `${this.productManufacturer?.product.name}`,
                        active: false,
                        url: '/app/products/edit/' + productId,
                    },
                    {
                        title: `${this.pricingEnabled ? 'Edit Pricing' : 'Manage Manufacturer'}: ${this.productManufacturer?.manufacturer.name}`,
                        active: true,
                    },
                ];

                this.manufacturerForm = this.fb.group({
                    product_id: [productId, Validators.required],
                    manufacturer_id: [{
                        value: this.productManufacturer?.manufacturer.id,
                        disabled: true
                    }, Validators.required],
                    effective_from: [this.productManufacturer?.default_price?.effective_from ? new Date(this.productManufacturer?.default_price?.effective_from) : this.currentDate],
                    tier: [this.productManufacturer?.default_price?.tier, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    operative_cost: [this.productManufacturer?.default_price?.operative_cost, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    subcontractor_cost: [this.productManufacturer?.default_price?.subcontractor_cost, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    material_cost: [this.productManufacturer?.default_price?.material_cost, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    sell_price: [this.productManufacturer?.default_price?.sell_price, !this.isDynamicallyPriced ? [Validators.required, Validators.min(0)] : Validators.min(0)],
                    pricing_tiers: this.fb.array([])
                });

                this.loadPrices();
            },
            error: error => this.apiService.handleError(error)
        });
    }

    private refreshProductManufacturer(): void
    {
        if (!this.productManufacturer?.id) {
            return;
        }

        this.loadManufacturerProduct(this.manufacturerForm.getRawValue().product_id, this.productManufacturer.id);
    }
}
